import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from '@servicetim/ui/components';
import type { ReactNode } from 'react';

export interface SelectOption {
  value: string;
  label: ReactNode;
  disabled?: boolean;
  /** Optional group heading; options with the same group render together. */
  group?: string;
}

export interface SimpleSelectProps {
  id?: string;
  value: string | null | undefined;
  onChange: (value: string | undefined) => void;
  options: readonly SelectOption[];
  placeholder?: string;
  /** Label of an explicit "none" entry; without it the select cannot be cleared. */
  emptyLabel?: string;
  disabled?: boolean;
  invalid?: boolean;
  size?: 'default' | 'sm';
  className?: string;
  'aria-label'?: string;
  'aria-describedby'?: string;
}

const NONE = '__none__';

/** Radix select over `{ value, label }` options with optional grouping and a clearable "none". */
export function SimpleSelect({
  id,
  value,
  onChange,
  options,
  placeholder = 'Select…',
  emptyLabel,
  disabled,
  invalid,
  size,
  className,
  ...aria
}: SimpleSelectProps) {
  const current = value ?? '';
  const orphan = current !== '' && !options.some((o) => o.value === current);
  const groups = new Map<string | undefined, SelectOption[]>();
  for (const option of options) {
    const list = groups.get(option.group) ?? [];
    list.push(option);
    groups.set(option.group, list);
  }
  return (
    <Select
      value={current}
      onValueChange={(next) => onChange(next === NONE ? undefined : next)}
      disabled={disabled}
    >
      <SelectTrigger
        id={id}
        size={size}
        className={className}
        aria-invalid={invalid || undefined}
        aria-label={aria['aria-label']}
        aria-describedby={aria['aria-describedby']}
      >
        <SelectValue placeholder={placeholder} />
      </SelectTrigger>
      <SelectContent>
        {emptyLabel ? (
          <SelectItem value={NONE}>
            <span className="text-fg-muted">{emptyLabel}</span>
          </SelectItem>
        ) : null}
        {[...groups.entries()].map(([group, items]) => (
          <SelectGroup key={group ?? '__ungrouped'}>
            {group ? <SelectLabel>{group}</SelectLabel> : null}
            {items.map((o) => (
              <SelectItem key={o.value} value={o.value} disabled={o.disabled}>
                {o.label}
              </SelectItem>
            ))}
          </SelectGroup>
        ))}
        {orphan ? (
          <SelectItem value={current} disabled>
            <span className="text-fg-muted italic">Unknown ({current.slice(0, 8)}…)</span>
          </SelectItem>
        ) : null}
      </SelectContent>
    </Select>
  );
}
